import { Box, Collapse, Flex, Text } from '@chakra-ui/react'
import {
  addFieldRow,
  bloodPressureField,
  EncounterType,
  hasVitalsField,
  heightField,
  o2SaturationField,
  pulseField,
  respiratoryRateField,
  temperatureField,
  VisitModality,
  weightField,
} from '@hb/shared'
import { useMemo } from 'react'
import { useField } from 'react-final-form'
import { useFormWidth } from '../../../../components/forms/FinalForm/context'
import { FormElement } from '../../../../components/forms/Input'
import { SectionLabel } from '../../SectionLabel'
import { PrenatalVitalsAlerts } from './PrenatalVitalsAlerts'
import { VitalSourceSelect } from './VitalSourceSelect'

const getBmi = (weight: number, height: number) => {
  if (!weight || !height) return null
  return ((weight * 703) / (height * height)).toFixed(1)
}
const EncounterWeightInput = () => {
  const {
    input: { value: weight },
  } = useField<number>('data.vital.weight', { subscription: { value: true } })
  const {
    input: { value: height },
  } = useField<number>('data.vital.height', { subscription: { value: true } })
  const bmi = getBmi(weight, height)
  return (
    <Flex align="center" gap={2} w="100%">
      <Box flex={1} minW={0}>
        <FormElement field={weightField} name="data.vital.weight" />
      </Box>
      <Text pt={3} flex={1} minW={0} fontSize="sm" fontWeight={500}>
        BMI: {bmi}
      </Text>
    </Flex>
  )
}

const PregnancyEncounterVitalsBody = ({ isTelehealth }: { isTelehealth: boolean }) => {
  const { mobileLayout } = useFormWidth()
  return (
    <Box w="100%">
      <SectionLabel>Vitals</SectionLabel>
      {mobileLayout ? (
        <Flex flexFlow="column" gap={1.5} w="100%">
          <Box>
            <FormElement field={weightField} name="data.vital.weight" />
            <VitalSourceSelect vitalKey="weight" isTelehealth={isTelehealth} />
          </Box>
          <Box>
            <FormElement field={temperatureField} name="data.vital.temperature" />
            <VitalSourceSelect vitalKey="temperature" isTelehealth={isTelehealth} />
          </Box>
          <Box>
            <FormElement field={bloodPressureField} name="data.vital.bloodPressure" />
            <VitalSourceSelect vitalKey="bloodPressure" isTelehealth={isTelehealth} />
          </Box>
          <Box>
            <FormElement field={pulseField} name="data.vital.pulse" />
            <VitalSourceSelect vitalKey="pulse" isTelehealth={isTelehealth} />
          </Box>
          <Box>
            <FormElement field={o2SaturationField} name="data.vital.o2Saturation" />
            <VitalSourceSelect vitalKey="o2Saturation" isTelehealth={isTelehealth} />
          </Box>
          <PrenatalVitalsAlerts />
        </Flex>
      ) : (
        <Flex flexFlow="column" gap={2} w="100%">
          <Flex align="center" gap={3} w="100%">
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(temperatureField, { index: 0, flex: 1 })}
                name="data.vital.temperature"
              />
              <VitalSourceSelect vitalKey="temperature" isTelehealth={isTelehealth} />
            </Box>
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(weightField, { index: 0, flex: 1 })}
                name="data.vital.weight"
              />
              <VitalSourceSelect vitalKey="weight" isTelehealth={isTelehealth} />
            </Box>
          </Flex>
          <Flex align="center" gap={3} w="100%">
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(bloodPressureField, { index: 0, flex: 1 })}
                name="data.vital.bloodPressure"
              />
              <VitalSourceSelect vitalKey="bloodPressure" isTelehealth={isTelehealth} />
            </Box>
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(pulseField, { index: 0, flex: 1 })}
                name="data.vital.pulse"
              />
              <VitalSourceSelect vitalKey="pulse" isTelehealth={isTelehealth} />
            </Box>
          </Flex>
          <Flex align="center" gap={3} w="100%">
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(o2SaturationField, { index: 0, flex: 1 })}
                name="data.vital.o2Saturation"
              />
              <VitalSourceSelect vitalKey="o2Saturation" isTelehealth={isTelehealth} />
            </Box>
            <Box flex={1} minW={0} />
          </Flex>
          <PrenatalVitalsAlerts />
        </Flex>
      )}
    </Box>
  )
}

const PatientEncounterVitalsBody = () => {
  const { mobileLayout } = useFormWidth()

  return (
    <Box w="100%">
      <SectionLabel>Vitals</SectionLabel>
      {mobileLayout ? (
        <Flex flexFlow="column" gap={1.5} w="100%">
          <FormElement field={heightField} name="data.vital.height" />
          <EncounterWeightInput />
          <FormElement field={temperatureField} name="data.vital.temperature" />
          <FormElement field={bloodPressureField} name="data.vital.bloodPressure" />
          <FormElement field={pulseField} name="data.vital.pulse" />
          <FormElement field={respiratoryRateField} name="data.vital.respiratoryRate" />
        </Flex>
      ) : (
        <Flex flexFlow="column" gap={2} w="100%">
          <Flex align="center" gap={3} w="100%">
            <Box flex={1} minW={0}>
              <FormElement field={heightField} name="data.vital.height" />
            </Box>
            <Box flex={1} minW={0}>
              <EncounterWeightInput />
            </Box>
          </Flex>

          <Flex align="center" gap={3} w="100%">
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(temperatureField, { index: 0, flex: 1 })}
                name="data.vital.temperature"
              />
            </Box>
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(bloodPressureField, { index: 0, flex: 1 })}
                name="data.vital.bloodPressure"
              />
            </Box>
          </Flex>
          <Flex align="center" gap={3} w="100%">
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(pulseField, { index: 0, flex: 1 })}
                name="data.vital.pulse"
              />
            </Box>
            <Box flex={1} minW={0}>
              <FormElement
                field={addFieldRow(respiratoryRateField, { index: 0, flex: 1 })}
                name="data.vital.respiratoryRate"
              />
            </Box>
          </Flex>
        </Flex>
      )}
    </Box>
  )
}

export const EncounterVitalsInput = ({
  optional,
  examType,
}: {
  optional?: boolean
  examType: EncounterType
}) => {
  const {
    input: { value: hasVitals },
  } = useField<boolean | undefined>('data.hasVitals', {
    subscription: { value: true },
  })

  const {
    input: { value: modality },
  } = useField<VisitModality | undefined>('data.visitLocation.modality', {
    subscription: { value: true },
  })

  const isPregnancyExam = useMemo(
    () => [EncounterType.ANTEPARTUM, EncounterType.POSTPARTUM].includes(examType),
    [examType],
  )

  // §H: Source dropdown only appears on telehealth visits.
  const isTelehealth = modality === 'telehealth-video' || modality === 'telehealth-audio-only'

  return (
    <Box data-child-name="data.vital" w="100%" px={4} py={3}>
      {optional ? (
        <Box px={1}>
          <FormElement field={hasVitalsField} name="data.hasVitals" />
        </Box>
      ) : null}
      <Collapse style={{ width: '100%' }} in={!!hasVitals || !optional}>
        <Box px={1} pb={1}>
          {isPregnancyExam ? (
            <PregnancyEncounterVitalsBody isTelehealth={isTelehealth} />
          ) : (
            <PatientEncounterVitalsBody />
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
